/* eslint-disable complexity */

import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCrudActivityContext } from '@futrli/shared-services';
import {
  AiAssumption,
  AvailableLineData,
  ChartOfAccountsAccount,
  FinancialConfig,
  PreviewAssumptionImpactsResponse
} from '@futrli-sdk/predict';

import { ASSUMPTION_URL_NAME, PAYMENT_TYPES } from '../../../core/constants';
import { AllowedAssumptionTypes, AllowedPaymentTypes } from '../../detail/reports/actions/actions';
import { createAssumption, deleteAssumption, updateAssumption } from '../../../core/api/assumption';
import { MultiPaymentTreatment, PaymentTreatment, PredictionType } from '../types';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { usePredictionBuilderFields } from '../hooks/use-prediction-builder-fields';
import { usePredictionsInfoContext } from '../../detail/context/predictions-info-context';
import { convertUrlType, getParsedAccounts, getPaymentDataSaveParams, getPredictionTypeArg } from './utils';
import { useLocalisedConstantsContext } from '../../../../../localisation/src/core/localised-constants-context';
import { useLoadPredictionBuilderData } from '../hooks/useLoadPredictionBuilderData/useLoadPredictionBuilderData';
import { useUrlPredictionInfo } from '../hooks/useUrlPredictionInfo/useUrlPredictionInfo';

interface StandardOrganisationPredictionBuilderContextProps {
  children?: ReactNode;
  urlPredictionId?: string;
  urlPredictionType?: string;
  isQuickCopy?: boolean;
  onClose?: () => void;
}

interface StandardOrganisationPredictionBuilderContextValues {
  accountsData?: ChartOfAccountsAccount[];
  accountIndex: string;
  financialConfig?: FinancialConfig;
  handleAccountIndexChange: (value: ChartOfAccountsAccount | null) => void;
  handleDeletePrediction: () => Promise<void>;
  handleAddNewMultiPaymentTreatment: () => void;
  handleChangeMultiPaymentTreatmentData: (index: number, data: MultiPaymentTreatment) => void;
  handleNonCashAccountIndexChange: (value: ChartOfAccountsAccount | null) => void;
  handlePaymentTermsChange: (value: string) => void;
  handlePaymentTreatmentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePredictionNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePredictionTypeChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSaveExisting: (
    params: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>
  ) => Promise<boolean>;
  handleSaveNew: (
    params: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>
  ) => Promise<AllowedAssumptionTypes | undefined>;
  handleVatPercentageChange: (value: string) => void;
  impactPreviewParams?: Partial<AllowedAssumptionTypes>;
  isCashFlowDisabled: boolean;
  isEditing: boolean;
  isLoading: boolean;
  isQuickCopy: boolean;
  isSaveDisabled: boolean;
  lineData?: AvailableLineData;
  multiPaymentData: MultiPaymentTreatment[];
  nonCashAccountIndex: string;
  paymentDataParams: Partial<AllowedPaymentTypes>;
  paymentDataSaveParams: Partial<AllowedAssumptionTypes['payment_type_data']>;
  paymentTerms: string;
  paymentTreatment: PaymentTreatment;
  predictionContentTouched: boolean;
  predictionData?: AllowedAssumptionTypes;
  predictionName: string;
  predictionType: PredictionType;
  reloadForecastData: () => void;
  reset: () => void;
  resetUrlData: () => void;
  setAccountIndex: (input: string) => void;
  setImpactPreviewParams: (input?: Partial<AllowedAssumptionTypes>) => void;
  setIsSaveDisabled: (disabled: boolean) => void;
  setPredictionContentTouched: (input: boolean) => void;
  setPredictionType: (input: PredictionType) => void;
  setVatPercentage: (input: string) => void;
  urlPredictionId?: string;
  urlPredictionType?: string;
  vatPercentage: string;
  previewImpactsResponse?: PreviewAssumptionImpactsResponse;
  handlePreviewImpactResponseChange: (previewImpactsResponse: PreviewAssumptionImpactsResponse) => void;
  hideAccountsImpacted: boolean;
  setHideAccountsImpacted: (hide: boolean) => void;
}

const context = createContext(({} as unknown) as StandardOrganisationPredictionBuilderContextValues);

export const StandardOrganisationPredictionBuilderContext = ({
  children,
  onClose,
  urlPredictionId: urlPredictionIdArg,
  urlPredictionType: urlPredictionTypeArg,
  isQuickCopy: isQuickCopyArg
}: StandardOrganisationPredictionBuilderContextProps) => {
  const { accountCategoryNames } = useLocalisedConstantsContext();
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { addUpdating, addCreating, removeCreating } = useCrudActivityContext();
  const { forecast } = usePredictionsInfoContext();

  const { urlPredictionId, urlPredictionType, resetUrlData } = useUrlPredictionInfo(
    urlPredictionIdArg,
    urlPredictionTypeArg
  );

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const forecastId = forecast?.id;
  const isEditing = !isQuickCopyArg && !!urlPredictionId && !!urlPredictionType;

  const isQuickCopy = !!isQuickCopyArg && !!urlPredictionId && !!urlPredictionType;

  const onlyShowProfitAndLoss = forecast?.kind === 'budget';

  const [previewImpactsResponse, setPreviewImpactResponse] = useState<PreviewAssumptionImpactsResponse>();

  const {
    chartOfAccounts,
    financialConfig,
    isLoading,
    lineData,
    paymentData,
    predictionData: loadedPredictionData,
    reloadForecastData,
    reloadLineData
  } = useLoadPredictionBuilderData(urlPredictionId, urlPredictionType);
  const predictionData = isEditing || isQuickCopy ? loadedPredictionData : undefined;

  const {
    accountIndex,
    handleAccountIndexChange,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    handlePredictionNameChange,
    handlePredictionTypeChange,
    handleVatPercentageChange,
    impactPreviewParams,
    isCashFlowDisabled,
    isSaveDisabled,
    multiPaymentData,
    nonCashAccountIndex,
    paymentData: paymentDataParams,
    paymentTerms,
    paymentTreatment,
    paymentType,
    predictionContentTouched,
    predictionName,
    predictionType,
    reset,
    setAccountIndex,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    setPredictionType,
    setVatPercentage,
    vatPercentage
  } = usePredictionBuilderFields(
    predictionData,
    paymentData,
    predictionData?.payment_type.type as PAYMENT_TYPES,
    convertUrlType(urlPredictionType)
  );

  const isInvalid = !predictionName || !accountIndex || isCashFlowDisabled;
  const [hideAccountsImpacted, setHideAccountsImpacted] = useState<boolean>(false);

  const paymentDataSaveParams = useMemo(() => {
    return getPaymentDataSaveParams(paymentDataParams, paymentType);
  }, [paymentDataParams, paymentType]);

  const accountsData = useMemo(() => {
    if (chartOfAccounts) {
      return getParsedAccounts(chartOfAccounts?.all_accounts, onlyShowProfitAndLoss, accountCategoryNames);
    }
  }, [chartOfAccounts, onlyShowProfitAndLoss, accountCategoryNames]);

  // *********
  // * Utils *
  // *********

  const handlePreviewImpactResponseChange = useCallback(
    (newPreviewImpactResponse: PreviewAssumptionImpactsResponse) => {
      setPreviewImpactResponse(newPreviewImpactResponse);
    },
    []
  );

  // ************
  // * Handlers *
  // ************

  const handleDeletePrediction = useCallback(async () => {
    if (urlPredictionId && urlPredictionType && forecastId && !isQuickCopy) {
      try {
        await deleteAssumption(
          workspaceId,
          organisationId,
          forecastId,
          ASSUMPTION_URL_NAME[urlPredictionType],
          urlPredictionId
        );

        reloadForecastData();

        onClose?.();
      } catch (e) {
        addErrorMessage?.(e as Error);
      }
    }
  }, [
    addErrorMessage,
    forecastId,
    isQuickCopy,
    onClose,
    organisationId,
    reloadForecastData,
    urlPredictionId,
    urlPredictionType,
    workspaceId
  ]);

  const handleSaveExisting = useCallback(
    async (params: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>) => {
      if (forecastId && params && urlPredictionId) {
        const fullParams = {
          name: predictionName,
          vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
          account_id: accountIndex,
          excluded_from_report: false,
          payment_type_data: paymentDataSaveParams,
          ...params
        };
        try {
          await addUpdating(
            'prediction',
            updateAssumption(
              fullParams,
              workspaceId,
              organisationId,
              forecastId,
              getPredictionTypeArg(predictionType),
              urlPredictionId
            )
          );
          return true;
        } catch (e) {
          addErrorMessage?.(e as Error);
        }
      }
      return false;
    },
    [
      accountIndex,
      addErrorMessage,
      addUpdating,
      forecastId,
      organisationId,
      paymentDataSaveParams,
      paymentTreatment,
      predictionName,
      predictionType,
      urlPredictionId,
      vatPercentage,
      workspaceId
    ]
  );

  const handleSaveNew = useCallback(
    async (params: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>) => {
      if (forecastId) {
        const fullParams = {
          name: predictionName,
          vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
          account_id: accountIndex,
          excluded_from_report: false,
          payment_type_data: paymentDataSaveParams,
          ...params
        };
        try {
          addCreating('prediction');
          const newAssumption = await createAssumption<AllowedAssumptionTypes>(
            fullParams,
            workspaceId,
            organisationId,
            forecastId,
            getPredictionTypeArg(predictionType)
          );
          reloadLineData();
          return newAssumption;
        } catch (e) {
          addErrorMessage?.(e as Error);
        } finally {
          removeCreating('prediction');
        }
      }
    },
    [
      accountIndex,
      addCreating,
      addErrorMessage,
      forecastId,
      organisationId,
      paymentDataSaveParams,
      paymentTreatment,
      predictionName,
      predictionType,
      reloadLineData,
      removeCreating,
      vatPercentage,
      workspaceId
    ]
  );

  const values = {
    accountsData,
    accountIndex,
    financialConfig,
    handleAccountIndexChange,
    handleDeletePrediction,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    handlePredictionNameChange,
    handlePredictionTypeChange,
    handleSaveExisting,
    handleSaveNew,
    handleVatPercentageChange,
    impactPreviewParams,
    isCashFlowDisabled,
    isEditing,
    isLoading,
    isQuickCopy,
    isSaveDisabled: isSaveDisabled || isInvalid,
    lineData,
    multiPaymentData,
    nonCashAccountIndex,
    paymentDataParams,
    paymentDataSaveParams,
    paymentTerms,
    paymentTreatment,
    predictionContentTouched,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setAccountIndex,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    setPredictionType,
    setVatPercentage,
    urlPredictionId,
    urlPredictionType,
    vatPercentage,
    previewImpactsResponse,
    handlePreviewImpactResponseChange,
    hideAccountsImpacted,
    setHideAccountsImpacted
  };

  return <context.Provider value={values}>{children}</context.Provider>;
};

export const useStandardOrganisationPredictionBuilderContext = () => useContext(context);
